import { z } from "zod";
import { cents, dateStr, flag, id, nonNegCents, nullableText } from "./common";

export const venueInput = z.object({
  name: z.string().trim().min(1).max(120),
  shortName: z.string().trim().min(1).max(40),
  slug: z.string().trim().regex(/^[a-z0-9-]+$/).max(80).optional(),
  address: z.string().trim().max(200).nullable().optional(),
  city: z.string().trim().max(80).nullable().optional(),
  state: z.string().trim().max(40).nullable().optional(),
  zip: z.string().trim().max(20).nullable().optional(),
  color: z.string().regex(/^#[0-9a-fA-F]{6}$/).optional(),
  capacity: z.number().int().min(0).nullable().optional(),
  damageDepositDefault: nonNegCents.optional(),
  defaultAssigneeMemberId: id.nullable().optional(),
  active: flag.optional(),
  sort: z.number().int().optional(),
  notes: nullableText.optional(),
  aliases: z.array(z.string().trim().min(1).max(120)).optional(),
});
export type VenueInput = z.infer<typeof venueInput>;
export const venuePatch = venueInput.partial();
export type VenuePatch = z.infer<typeof venuePatch>;

export const namedRowInput = z.object({
  name: z.string().trim().min(1).max(120),
  active: flag.optional(),
  sort: z.number().int().optional(),
});
export const eventTypeInput = namedRowInput;
export type EventTypeInput = z.infer<typeof eventTypeInput>;
export const eventTypePatch = namedRowInput.partial();

export const leadSourceInput = namedRowInput.extend({ autoAck: flag.optional() });
export type LeadSourceInput = z.infer<typeof leadSourceInput>;
export const leadSourcePatch = leadSourceInput.partial();

export const catalogKind = z.enum(["package", "fee", "addon", "discount"]);
export const catalogPricing = z.enum(["flat", "hourly", "per_guest"]);
export const dayRates = z
  .object({ fri_sun: cents.optional(), sat: cents.optional(), weekday: cents.optional() })
  .nullable();

export const catalogItemRecord = z.object({
  venueId: id.nullable().optional(),
  name: z.string().trim().min(1).max(160),
  description: nullableText.optional(),
  kind: catalogKind,
  pricing: catalogPricing,
  pricingAction: z.enum(["pay_in_full_discount", "late_fee"]).nullable().optional(),
  defaultAmount: cents,
  dayRates: dayRates.optional(),
  minHours: z.number().int().min(0).nullable().optional(),
  includedHours: z.number().int().min(0).nullable().optional(),
  autoAddWithPackage: flag.optional(),
  active: flag.optional(),
  sort: z.number().int().optional(),
});
export const catalogItemInput = catalogItemRecord.extend({ pricing: catalogPricing.default("flat"), defaultAmount: cents.default(0) });
export type CatalogItemInput = z.infer<typeof catalogItemInput>;
export const catalogItemPatch = catalogItemRecord.partial();
export type CatalogItemPatch = z.infer<typeof catalogItemPatch>;

export const catalogListQuery = z.object({
  venueId: id.optional(),
  date: dateStr.optional(),
  includeInactive: z
    .enum(["0", "1"])
    .optional()
    .transform((v) => v === "1"),
});
